import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveNote,
  autoTitleNote,
  createNote,
  deleteNote,
  duplicateNote,
  listNotes,
  purgeNote,
  restoreNote,
  togglePinNote,
  updateNote,
} from '../api/client';
import type {
  CreateNoteRequest,
  NoteListResponse,
  NoteResponse,
  UpdateNoteRequest,
} from '../api/types';

const NOTES_KEY = ['notes'] as const;

export function useNotes(view: string = 'active') {
  return useQuery({
    queryKey: [...NOTES_KEY, view],
    queryFn: () => listNotes(view),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

function replaceInList(
  list: NoteListResponse | undefined,
  updated: NoteResponse,
): NoteListResponse {
  const notes = list?.notes ?? [];
  const next = notes.some((n) => n.id === updated.id)
    ? notes.map((n) => (n.id === updated.id ? updated : n))
    : [updated, ...notes];
  return { notes: sortNotes(next) };
}

function removeFromList(
  list: NoteListResponse | undefined,
  id: string,
): NoteListResponse {
  return { notes: (list?.notes ?? []).filter((n) => n.id !== id) };
}

/**
 * Mirrors NoteRepository.SortKey on the backend so optimistic updates land in
 * the same order the server would return them: pinned bucket first (most
 * recent pin wins), then everything else by updatedAt descending.
 */
function sortNotes(notes: NoteResponse[]): NoteResponse[] {
  return [...notes].sort((a, b) => {
    const aPinned = !!a.pinnedAt;
    const bPinned = !!b.pinnedAt;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    const aKey = aPinned ? a.pinnedAt! : a.updatedAt;
    const bKey = bPinned ? b.pinnedAt! : b.updatedAt;
    return bKey.localeCompare(aKey);
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateNoteRequest) => createNote(req),
    onSuccess: (note) => {
      qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], (prev) => replaceInList(prev, note));
    },
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, req }: { id: string; req: UpdateNoteRequest }) => updateNote(id, req),
    onSuccess: (note) => {
      qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], (prev) => replaceInList(prev, note));
    },
  });
}

export function useTogglePinNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => togglePinNote(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTES_KEY });
      const previous = qc.getQueryData<NoteListResponse>([...NOTES_KEY, 'active']);
      if (previous) {
        const now = new Date().toISOString();
        const next: NoteListResponse = {
          notes: sortNotes(
            previous.notes.map((n) =>
              n.id === id
                ? { ...n, pinnedAt: n.pinnedAt ? null : now, updatedAt: now }
                : n,
            ),
          ),
        };
        qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], next);
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData([...NOTES_KEY, 'active'], ctx.previous);
    },
    onSuccess: (note) => {
      qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], (prev) => replaceInList(prev, note));
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteNote(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTES_KEY });
      const previous = qc.getQueryData<NoteListResponse>([...NOTES_KEY, 'active']);
      if (previous) {
        qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], removeFromList(previous, id));
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData([...NOTES_KEY, 'active'], ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
    },
  });
}

export function useArchiveNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveNote(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTES_KEY });
      const previous = qc.getQueryData<NoteListResponse>([...NOTES_KEY, 'active']);
      if (previous) {
        qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], removeFromList(previous, id));
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData([...NOTES_KEY, 'active'], ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
    },
  });
}

export function useRestoreNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreNote(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTES_KEY });
      for (const view of ['archive', 'trash']) {
        const previous = qc.getQueryData<NoteListResponse>([...NOTES_KEY, view]);
        if (previous) {
          qc.setQueryData<NoteListResponse>([...NOTES_KEY, view], removeFromList(previous, id));
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
    },
  });
}

export function usePurgeNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purgeNote(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTES_KEY });
      const previous = qc.getQueryData<NoteListResponse>([...NOTES_KEY, 'trash']);
      if (previous) {
        qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'trash'], removeFromList(previous, id));
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData([...NOTES_KEY, 'trash'], ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
    },
  });
}

export function useDuplicateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicateNote(id),
    onSuccess: (note) => {
      qc.setQueryData<NoteListResponse>([...NOTES_KEY, 'active'], (prev) => replaceInList(prev, note));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
    },
  });
}

export function useAutoTitleNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => autoTitleNote(id),
    onSuccess: (result, id) => {
      for (const view of ['active', 'archive', 'trash']) {
        qc.setQueryData<NoteListResponse>([...NOTES_KEY, view], (prev) => {
          if (!prev) return prev;
          return {
            notes: prev.notes.map((n) =>
              n.id === id ? { ...n, title: result.title } : n,
            ),
          };
        });
      }
    },
  });
}
